import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type Ref } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  IconButton,
  InputBase,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Close, FirstPage, LastPage } from '@mui/icons-material';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key';
import { useCachedBlobUrl } from '@/hooks/use-cached-blob-url';
import { buildPresentationSlideCacheKey } from '@/components/preview/preview-presentation-slides-layer.helpers';
import { prefetchPreviewBlobEntries } from '@/helpers/preview/preview-blob-cache.helper';
import {
  resolveFirstFrameForSlide,
  resolvePresentationStepPosition,
} from '@/helpers/presentation-step-position.helper';

export interface PresentationSlideJumpModalProps {
  open: boolean;
  onClose: () => void;
  currentPage: number;
  totalPages: number;
  onJump: (page: number) => void;
  /** Slide preview image URLs (1-based order). Enables the image carousel. */
  slidePreviewUrls?: string[];
  itemId?: string;
  assetVersion?: string;
  /** Presentation step mode: original slide number of each page; the input then takes slide numbers. */
  frameSlideNumbers?: number[];
}

function parseSlideInput(value: string): number | undefined {
  if (value === '') {
    return undefined;
  }
  const num = Number.parseInt(value, 10);
  if (!Number.isFinite(num) || num < 1) {
    return undefined;
  }
  return num;
}

function SlideCarouselThumb({
  page,
  url,
  cacheKey,
  selected,
  onSelect,
  slideLabel,
  rootRef,
}: {
  page: number;
  url?: string;
  cacheKey?: string;
  selected: boolean;
  onSelect: () => void;
  slideLabel: string;
  rootRef?: Ref<HTMLButtonElement>;
}) {
  const { url: blobUrl, loading } = useCachedBlobUrl(url, cacheKey);

  return (
    <Box
      component="button"
      type="button"
      ref={rootRef}
      onClick={onSelect}
      aria-label={slideLabel}
      aria-current={selected ? 'true' : undefined}
      role="option"
      aria-selected={selected}
      sx={{
        'flex': '0 0 78%',
        'scrollSnapAlign': 'center',
        'scrollSnapStop': 'always',
        'aspectRatio': '16 / 9',
        'border': 2,
        'borderColor': selected ? 'primary.main' : 'divider',
        'borderRadius': 1,
        'overflow': 'hidden',
        'position': 'relative',
        'p': 0,
        'm': 0,
        'bgcolor': 'common.black',
        'cursor': 'pointer',
        'appearance': 'none',
        'WebkitAppearance': 'none',
        'transition': (theme) => theme.transitions.create(['border-color', 'box-shadow'], { duration: 150 }),
        'boxShadow': selected ? 3 : 0,
        '&:focus-visible': {
          outline: (theme) => `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      }}
    >
      {blobUrl ? (
        <Box
          component="img"
          src={blobUrl}
          alt={slideLabel}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            display: 'block',
            opacity: loading ? 0.7 : 1,
          }}
        />
      ) : (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'action.hover',
          }}
        >
          <Typography variant="h4" color="text.secondary">
            {page}
          </Typography>
        </Box>
      )}
      <Typography
        variant="caption"
        sx={{
          position: 'absolute',
          bottom: 6,
          right: 8,
          px: 0.75,
          py: 0.15,
          borderRadius: 0.5,
          bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.65)' : 'rgba(255,255,255,0.85)'),
          color: 'text.primary',
          fontWeight: 600,
          lineHeight: 1.4,
        }}
      >
        {page}
      </Typography>
    </Box>
  );
}

function PresentationSlideJumpForm({
  currentPage,
  totalPages,
  onJump,
  onClose,
  slidePreviewUrls = [],
  itemId,
  assetVersion,
  frameSlideNumbers,
}: {
  currentPage: number;
  totalPages: number;
  onJump: (page: number) => void;
  onClose: () => void;
  slidePreviewUrls?: string[];
  itemId?: string;
  assetVersion?: string;
  frameSlideNumbers?: number[];
}) {
  const t = useTranslations();
  const slideMap = frameSlideNumbers?.length === totalPages ? frameSlideNumbers : undefined;
  const slideTotal = slideMap ? Math.max(...slideMap) : totalPages;
  const slideOfPage = (page: number) => slideMap?.[page - 1] ?? page;
  const pageOfSlide = (slide: number) => (slideMap ? (resolveFirstFrameForSlide(slide, slideMap) ?? slide) : slide);
  const [selectedPage, setSelectedPage] = useState(currentPage);
  const [slideNumber, setSlideNumber] = useState(String(slideOfPage(currentPage)));
  const [inputError, setInputError] = useState<string | undefined>();
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const slideRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const ignoreScrollSyncRef = useRef(false);
  const scrollSyncTimerRef = useRef<number | null>(null);

  const slideCount = Math.max(totalPages, slidePreviewUrls.length);

  const scrollToPage = useCallback((page: number, behavior: ScrollBehavior = 'smooth') => {
    const scroller = scrollerRef.current;
    const el = slideRefs.current[page - 1];
    if (!scroller || !el) {
      return;
    }
    ignoreScrollSyncRef.current = true;
    const targetLeft = el.offsetLeft - (scroller.clientWidth - el.offsetWidth) / 2;
    scroller.scrollTo({ left: Math.max(0, targetLeft), behavior });
    if (scrollSyncTimerRef.current != null) {
      window.clearTimeout(scrollSyncTimerRef.current);
    }
    scrollSyncTimerRef.current = window.setTimeout(
      () => {
        ignoreScrollSyncRef.current = false;
        scrollSyncTimerRef.current = null;
      },
      behavior === 'smooth' ? 350 : 50,
    );
  }, []);

  const selectPage = useCallback(
    (page: number, options?: { scroll?: boolean; behavior?: ScrollBehavior }) => {
      const clamped = Math.min(Math.max(1, page), slideCount || 1);
      setSelectedPage(clamped);
      setSlideNumber(String(slideMap?.[clamped - 1] ?? clamped));
      setInputError(undefined);
      if (options?.scroll !== false) {
        scrollToPage(clamped, options?.behavior ?? 'smooth');
      }
    },
    [scrollToPage, slideCount, slideMap],
  );

  useEffect(() => {
    // Center the initial selection without animating on open.
    scrollToPage(currentPage, 'auto');
  }, [currentPage, scrollToPage]);

  useEffect(() => {
    if (!itemId || slidePreviewUrls.length === 0) {
      return;
    }
    const entries = slidePreviewUrls
      .map((url, index) => {
        const cacheKey = buildPresentationSlideCacheKey(itemId, assetVersion, index + 1);
        return url && cacheKey ? { url, cacheKey } : undefined;
      })
      .filter((entry): entry is { url: string; cacheKey: string } => Boolean(entry));
    void prefetchPreviewBlobEntries(entries);
  }, [itemId, assetVersion, slidePreviewUrls]);

  useEffect(() => {
    return () => {
      if (scrollSyncTimerRef.current != null) {
        window.clearTimeout(scrollSyncTimerRef.current);
      }
    };
  }, []);

  const handleScrollerScroll = () => {
    if (ignoreScrollSyncRef.current) {
      return;
    }
    const scroller = scrollerRef.current;
    if (!scroller) {
      return;
    }
    const centerX = scroller.scrollLeft + scroller.clientWidth / 2;
    let nearestPage = selectedPage;
    let nearestDist = Number.POSITIVE_INFINITY;
    slideRefs.current.forEach((el, index) => {
      if (!el) {
        return;
      }
      const slideCenter = el.offsetLeft + el.offsetWidth / 2;
      const dist = Math.abs(slideCenter - centerX);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearestPage = index + 1;
      }
    });
    if (nearestPage !== selectedPage) {
      setSelectedPage(nearestPage);
      setSlideNumber(String(slideOfPage(nearestPage)));
      setInputError(undefined);
    }
  };

  const handleJump = (page: number) => {
    onJump(page);
    onClose();
  };

  const pageLabel = (page: number) => {
    const position = resolvePresentationStepPosition(page, slideMap);
    return position
      ? t('media.slideControl.slideStepCounter' as TranslationKey, {
          slide: position.slideNumber,
          slides: position.slideTotal,
          step: position.step,
          steps: position.stepCount,
        })
      : t('media.slideControl.slideCounter' as TranslationKey, { current: page, total: totalPages });
  };

  const handleGoToSubmit = () => {
    const num = parseSlideInput(slideNumber);
    if (num === undefined) {
      setInputError(t('media.slideControl.invalidSlideNumber' as TranslationKey));
      return;
    }
    if (num > slideTotal) {
      setInputError(t('media.slideControl.slideNumberExceedsTotal' as TranslationKey, { total: slideTotal }));
      return;
    }
    // Typing a slide number already selects that slide's first step. A later carousel step of the same slide must be kept.
    handleJump(selectedPage);
  };

  const handleSlideNumberChange = (val: string) => {
    if (val === '' || (/^\d+$/.test(val) && Number(val) > 0)) {
      setSlideNumber(val);
      setInputError(undefined);
      const num = parseSlideInput(val);
      if (num != null && num <= slideTotal) {
        // Keep the typed digits. selectPage would replace them with the visible slide, so typing 20 becomes 30 when slide 2 is hidden.
        const page = Math.min(Math.max(1, pageOfSlide(num)), slideCount || 1);
        setSelectedPage(page);
        scrollToPage(page);
      }
    }
  };

  const handleSlideNumberKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleGoToSubmit();
      return;
    }
    if (
      ['e', 'E', '+', '-', '.'].includes(e.key) ||
      e.key === ' ' ||
      (!/^\d$/.test(e.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End'].includes(e.key))
    ) {
      e.preventDefault();
    }
  };

  return (
    <>
      <DialogContent sx={{ px: { xs: 1.5, sm: 3 }, pt: 1, pb: 1 }}>
        <Box
          ref={scrollerRef}
          onScroll={handleScrollerScroll}
          role="listbox"
          aria-label={t('media.slideControl.goToSlideDialogTitle' as TranslationKey)}
          sx={{
            'display': 'flex',
            'gap': 1.5,
            'position': 'relative',
            'overflowX': 'auto',
            'scrollSnapType': 'x mandatory',
            'WebkitOverflowScrolling': 'touch',
            'pb': 1.5,
            'mb': 2.5,
            'mx': { xs: -0.5, sm: 0 },
            'px': '11%',
            'scrollbarWidth': 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {Array.from({ length: slideCount }, (_, index) => {
            const page = index + 1;
            const url = slidePreviewUrls[index];
            const cacheKey = buildPresentationSlideCacheKey(itemId, assetVersion, page);
            return (
              <SlideCarouselThumb
                key={page}
                page={page}
                url={url}
                cacheKey={cacheKey}
                selected={selectedPage === page}
                onSelect={() => selectPage(page)}
                rootRef={(el) => {
                  slideRefs.current[index] = el;
                }}
                slideLabel={pageLabel(page)}
              />
            );
          })}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'stretch',
              width: '100%',
              maxWidth: 300,
              height: 56,
              borderRadius: 999,
              border: 1,
              borderColor: inputError ? 'error.main' : 'divider',
              bgcolor: (theme) =>
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.common.white, 0.06)
                  : alpha(theme.palette.common.black, 0.03),
              boxShadow: (theme) =>
                theme.palette.mode === 'dark' ? 'none' : `inset 0 1px 0 ${alpha(theme.palette.common.white, 0.7)}`,
              overflow: 'hidden',
            }}
          >
            <IconButton
              onClick={() => selectPage(1)}
              disabled={selectedPage <= 1}
              aria-label={t('media.slideControl.firstSlide' as TranslationKey)}
              sx={{
                'flex': '0 0 52px',
                'borderRadius': 0,
                'color': 'text.primary',
                'borderRight': 1,
                'borderColor': 'divider',
                '&:hover': { bgcolor: 'action.hover' },
                '&.Mui-disabled': { opacity: 0.35 },
              }}
            >
              <FirstPage />
            </IconButton>
            <Box
              sx={{
                flex: '1 1 auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.75,
                px: 1.5,
                minWidth: 0,
              }}
            >
              <InputBase
                value={slideNumber}
                onChange={(e) => handleSlideNumberChange(e.target.value)}
                onKeyDown={handleSlideNumberKeyDown}
                inputProps={{
                  'min': 1,
                  'max': slideTotal,
                  'inputMode': 'numeric',
                  'aria-label': t('media.slideControl.slideNumber' as TranslationKey),
                  'aria-invalid': Boolean(inputError),
                }}
                sx={{
                  'width': `${Math.max(2, String(slideTotal).length + 1)}ch`,
                  'color': inputError ? 'error.main' : 'text.primary',
                  '& input': {
                    p: 0,
                    textAlign: 'center',
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    lineHeight: 1.2,
                    fontVariantNumeric: 'tabular-nums',
                    letterSpacing: '-0.02em',
                  },
                }}
              />
              <Typography
                component="span"
                sx={{
                  color: 'text.secondary',
                  fontSize: '1rem',
                  fontWeight: 500,
                  fontVariantNumeric: 'tabular-nums',
                  whiteSpace: 'nowrap',
                  userSelect: 'none',
                }}
              >
                / {slideTotal}
              </Typography>
            </Box>
            <IconButton
              onClick={() => selectPage(pageOfSlide(slideTotal))}
              disabled={selectedPage >= pageOfSlide(slideTotal)}
              aria-label={t('media.slideControl.lastSlide' as TranslationKey)}
              sx={{
                'flex': '0 0 52px',
                'borderRadius': 0,
                'color': 'text.primary',
                'borderLeft': 1,
                'borderColor': 'divider',
                '&:hover': { bgcolor: 'action.hover' },
                '&.Mui-disabled': { opacity: 0.35 },
              }}
            >
              <LastPage />
            </IconButton>
          </Box>
          <FormHelperText
            error={Boolean(inputError)}
            sx={{
              m: 0,
              minHeight: 20,
              textAlign: 'center',
              visibility: inputError ? 'visible' : 'hidden',
            }}
          >
            {inputError || ' '}
          </FormHelperText>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1, justifyContent: 'center' }}>
        <Button onClick={onClose} color="inherit" sx={{ minWidth: 96 }}>
          {t('common.cancel' as TranslationKey)}
        </Button>
        <Button onClick={handleGoToSubmit} variant="contained" sx={{ minWidth: 96, borderRadius: 999, px: 3 }}>
          {t('media.slideControl.go' as TranslationKey)}
        </Button>
      </DialogActions>
    </>
  );
}

export function PresentationSlideJumpModal({
  open,
  onClose,
  currentPage,
  totalPages,
  onJump,
  slidePreviewUrls,
  itemId,
  assetVersion,
  frameSlideNumbers,
}: PresentationSlideJumpModalProps) {
  const t = useTranslations();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth disableAutoFocus>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        {t('media.slideControl.goToSlideDialogTitle' as TranslationKey)}
        <IconButton aria-label={t('common.close' as TranslationKey)} onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      {open ? (
        <PresentationSlideJumpForm
          key={`${currentPage}:${totalPages}`}
          currentPage={currentPage}
          totalPages={totalPages}
          onJump={onJump}
          onClose={onClose}
          slidePreviewUrls={slidePreviewUrls}
          itemId={itemId}
          assetVersion={assetVersion}
          frameSlideNumbers={frameSlideNumbers}
        />
      ) : null}
    </Dialog>
  );
}
